import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(null);
  const [permissionCache, setPermissionCache] = useState({});

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('auth_token');

        if (storedToken) {

          try {
            const decoded = jwtDecode(storedToken);
            const currentTime = Date.now() / 1000;

            if (decoded.exp && decoded.exp > currentTime) {

              setToken(storedToken);

              const role = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ||
                decoded.role;

              setUserInfo({
                userId: decoded.sub,
                username: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] || decoded.name,
                email: decoded.email,
                role: role,
              });
              setIsLoggedIn(true);

              await verifyToken(storedToken);
            } else {
              localStorage.removeItem('auth_token');
              cleanupAuth();
            }
          } catch (error) {
            localStorage.removeItem('auth_token');
            cleanupAuth();
          }
        }

        const csrfCookie = getCookie('XSRF-TOKEN');
        if (csrfCookie) {
          setCsrfToken(csrfCookie);
        } else {
          await refreshCsrfToken();
        }
      } catch (error) {
        cleanupAuth();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/Users/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Token verification failed');
      }

      return true;
    } catch (error) {
      localStorage.removeItem('auth_token');
      cleanupAuth();
      return false;
    }
  };

  const refreshCsrfToken = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/Users/csrf/refresh`, {
        method: 'GET',
        credentials: 'include',
      });

      const csrfCookie = getCookie('XSRF-TOKEN');
      if (csrfCookie) {
        setCsrfToken(csrfCookie);
      }
    } catch (error) {
      // Silent fail (previously only 1 console.error line in this catch block, no other actions)
    }
  }, []);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const cleanupAuth = () => {
    setToken(null);
    setUserInfo(null);
    setIsLoggedIn(false);
  };

  const login = async (credentials, loginSource = "user") => {
    try {

      if (!csrfToken) {
        await refreshCsrfToken();
      }

      const response = await fetch(`${API_BASE_URL}/Users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': csrfToken,
          'X-Login-Source': loginSource
        },
        body: JSON.stringify(credentials),
        credentials: 'include',
      });

      if (response.status === 429) {
        const error = new Error('Too many login attempts. Please try again later.');
        error.status = 429;
        throw error;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();

      localStorage.setItem('auth_token', data.token);

      setToken(data.token);
      setUserInfo(data.user);
      setIsLoggedIn(true);

      const csrfCookie = getCookie('XSRF-TOKEN');
      if (csrfCookie) {
        setCsrfToken(csrfCookie);
      }

      return data.user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await refreshCsrfToken();
        await authFetch(`${API_BASE_URL}/Users/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-XSRF-TOKEN': csrfToken,
          },
          credentials: 'include',
        }).catch(() => {
          // Silent fail (previously only 1 console.error line in this catch block, no other actions)
        });
      }
    } finally {
      localStorage.removeItem('auth_token');
      clearPermissionCache();
      cleanupAuth();
    }
  };

  const authFetch = async (url, options = {}) => {
    try {

      const headers = {
        ...(options.headers || {}),
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method)) {
        // Always read cookie first — state may be stale after backend restart
        let currentCsrf = getCookie('XSRF-TOKEN');
        if (!currentCsrf) {
          await refreshCsrfToken();
          currentCsrf = getCookie('XSRF-TOKEN');
        }
        if (currentCsrf) {
          headers['X-XSRF-TOKEN'] = currentCsrf;
          setCsrfToken(currentCsrf);
        }
      }

      let response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      // If CSRF token was rejected, refresh and retry once
      if (response.status === 403 && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method)) {
        const errData = await response.json().catch(() => null);
        if (errData?.message?.toLowerCase().includes('csrf')) {
          await refreshCsrfToken();
          const freshCsrf = getCookie('XSRF-TOKEN');
          if (freshCsrf) {
            headers['X-XSRF-TOKEN'] = freshCsrf;
            setCsrfToken(freshCsrf);
            response = await fetch(url, { ...options, headers, credentials: 'include' });
          }
        }
      }

      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        cleanupAuth();
        throw new Error('Authentication failed. Please log in again.');
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  const isAdmin = async () => {
    if (!token) return false;
    if (permissionCache.hasOwnProperty('isAdmin')) {
      return permissionCache.isAdmin;
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/Users/role/admin`);
      if (!response.ok) return false;

      const data = await response.json();
      setPermissionCache(prev => ({
        ...prev,
        isAdmin: data.isInRole
      }));
      return data.isInRole;
    } catch (error) {
      return false;
    }
  };

  const isAnyAdmin = async () => {
    if (!token) return false;
    if (permissionCache.hasOwnProperty('isAnyAdmin')) {
      return permissionCache.isAnyAdmin;
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/Users/role/any-admin`);
      if (!response.ok) return false;

      const data = await response.json();
      setPermissionCache(prev => ({
        ...prev,
        isAnyAdmin: data.isInRole
      }));
      return data.isInRole;
    } catch (error) {
      return false;
    }
  };

  const hasAccess = async (feature) => {
    if (!token) return false;
    const adminStatus = await isAdmin();
    if (adminStatus) return true;

    const cacheKey = `feature_${feature}`;
    if (permissionCache.hasOwnProperty(cacheKey)) {
      return permissionCache[cacheKey];
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/Users/permissions/${feature}`);
      if (!response.ok) return false;

      const data = await response.json();
      setPermissionCache(prev => ({
        ...prev,
        [cacheKey]: data.hasAccess
      }));
      return data.hasAccess;
    } catch (error) {
      return false;
    }
  };

  const clearPermissionCache = () => {
    setPermissionCache({});
  };

  useEffect(() => {
    if (!token) return;

    try {
      const decoded = jwtDecode(token);

      const expiryTime = decoded.exp;
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = expiryTime - currentTime;

      if (timeUntilExpiry < 300) {
        const refreshToken = async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/Users/refresh`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': csrfToken,
              },
              credentials: 'include',
            });

            if (response.ok) {
              const data = await response.json();
              localStorage.setItem('auth_token', data.token);
              setToken(data.token);
            } else {
              localStorage.removeItem('auth_token');
              cleanupAuth();
            }
          } catch (error) {
            localStorage.removeItem('auth_token');
            cleanupAuth();
          }
        };

        refreshToken();
      }
    } catch (error) {
      localStorage.removeItem('auth_token');
      cleanupAuth();
    }
  }, [token, csrfToken]);

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      userInfo,
      login,
      logout,
      isAnyAdmin,
      isAdmin,
      hasAccess,
      loading,
      authFetch,
      refreshCsrfToken,
      csrfToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};